import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { parseSampleDecimal, sampleDecimalValue } from 'src/app/utils/sample-decimal';
import { BatchCreateSamplesDataService, BatchSampleField } from './batch-create-samples-data.service';
import { SampleService } from 'src/app/services/sample.service';
import { SampleGroupService } from 'src/app/services/sample-group.service';
import { NavBarComponent } from 'src/app/nav-bar/nav-bar.component';
import { HelperService } from 'src/app/helper.service';

export interface SamplePreviewRow {
  farmName: string;
  fieldName: string;
  fieldId: number;
  sampleName: string;
  // Soft tissue fields
  plantPartId: number | null;
  note: string;
  // Soil fields
  sampleDepth: string;
  rockPercentage: string;
}

@Component({
  selector: 'app-batch-create-samples',
  templateUrl: './batch-create-samples.component.html',
  styleUrls: ['./batch-create-samples.component.css'],
  standalone: false
})
export class BatchCreateSamplesComponent implements OnInit {
  currentStep = 1;
  sampleType: 'soft_tissue' | 'soil' | null = null;
  groupName = '';
  startingNumber = '';
  previewRows: SamplePreviewRow[] = [];
  plantParts: any[] = [];

  selectedFields: BatchSampleField[] = [];
  private seasonId = 0;

  // Batch update values
  batchPlantPartId: number | null = null;
  batchSampleDepth = '';
  batchRockPercentage = '';
  batchNote = '';

  // Row selection
  selectedRowIds = new Set<number>();

  get allSelected(): boolean { return this.previewRows.length > 0 && this.selectedRowIds.size === this.previewRows.length; }
  get someSelected(): boolean { return this.selectedRowIds.size > 0 && !this.allSelected; }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dataService: BatchCreateSamplesDataService,
    private sampleService: SampleService,
    private sampleGroupService: SampleGroupService,
    private nav: NavBarComponent,
    private snackBar: MatSnackBar,
    private helper: HelperService
  ) {}

  ngOnInit(): void {
    this.selectedFields = this.dataService.selectedFields;
    this.seasonId = this.dataService.seasonId;

    if (!this.selectedFields.length) {
      this.goBack();
      return;
    }

    this.sampleService.getSoftTissuePlantPartList().subscribe(res => this.plantParts = res || []);
  }

  selectType(type: 'soft_tissue' | 'soil'): void {
    this.sampleType = type;
    this.currentStep = 2;
  }

  generatePreview(): void {
    if (!this.groupName.trim() || !this.startingNumber.trim()) {
      this.snackBar.open('Please fill in all fields', 'CLOSE', { duration: 3000 });
      return;
    }

    const parsed = this.parseNumber(this.startingNumber);
    if (!parsed) {
      this.snackBar.open('Invalid starting number. Use e.g. S01', 'CLOSE', { duration: 3000 });
      return;
    }

    this.previewRows = this.selectedFields.map((field, i) => ({
      farmName: field.farmName,
      fieldName: field.fieldName,
      fieldId: field.fieldId,
      sampleName: this.formatNumber(parsed.prefix, parsed.num + i, parsed.pad),
      plantPartId: null,
      note: '',
      sampleDepth: '',
      rockPercentage: ''
    }));

    this.currentStep = 3;
  }

  submitSamples(): void {
    // Checked before anything is created, and named by row, because this screen can submit
    // fifty samples at once and a rejected batch should say which line to fix. A decimal
    // comma is what an agent types and a numeric column will not take it.
    if (this.sampleType === 'soil') {
      const badRow = this.previewRows.findIndex(row =>
        parseSampleDecimal(row.sampleDepth) === 'invalid' ||
        parseSampleDecimal(row.rockPercentage) === 'invalid');
      if (badRow > -1) {
        this.snackBar.open(
          'Row ' + (badRow + 1) + ': depth and rock % must be numbers, for example 2,5',
          'CLOSE', { duration: 5000 });
        return;
      }
    }

    this.nav.toggleLoading(true);
    const tenantId = this.helper.getTenantId();
    const userId = this.helper.getUserId();

    // Create sample group first, then batch create all samples at field centroids
    this.sampleGroupService.insertSampleGroup(this.groupName, tenantId, userId, false)
      .subscribe({
        next: (group: any) => {
          const samples = this.previewRows.map(row => ({
            fieldId: row.fieldId,
            name: row.sampleName,
            note: row.note || null,
            ...(this.sampleType === 'soil' ? {
              sampleDepth: sampleDecimalValue(row.sampleDepth),
              rockPercentage: sampleDecimalValue(row.rockPercentage)
            } : {
              plantPartId: row.plantPartId || null
            })
          }));

          const apiSampleType = this.sampleType === 'soft_tissue' ? 'softtissue' : 'soil';

          this.sampleService.batchCreateSamples(tenantId, group.id, apiSampleType, samples)
            .subscribe({
              next: () => {
                this.nav.toggleLoading(false);
                this.snackBar.open(`${this.previewRows.length} sample(s) created`, 'OK', { duration: 3000 });
                this.dataService.clear();
                this.goBack();
              },
              error: () => {
                this.nav.toggleLoading(false);
                this.snackBar.open('Error creating samples', 'CLOSE', { duration: 3000 });
              }
            });
        },
        error: () => {
          this.nav.toggleLoading(false);
          this.snackBar.open('Error creating sample group', 'CLOSE', { duration: 3000 });
        }
      });
  }

  applyBatchValues(): void {
    this.previewRows.forEach((row, i) => {
      if (!this.selectedRowIds.has(i)) return;
      if (this.sampleType === 'soft_tissue' && this.batchPlantPartId !== null) {
        row.plantPartId = this.batchPlantPartId;
      }
      if (this.sampleType === 'soil') {
        if (this.batchSampleDepth) row.sampleDepth = this.batchSampleDepth;
        if (this.batchRockPercentage) row.rockPercentage = this.batchRockPercentage;
      }
      if (this.batchNote) row.note = this.batchNote;
    });
    this.clearBatchValues();
  }

  clearBatchValues(): void {
    this.batchPlantPartId = null;
    this.batchSampleDepth = '';
    this.batchRockPercentage = '';
    this.batchNote = '';
  }

  toggleRowSelection(index: number): void {
    if (this.selectedRowIds.has(index)) {
      this.selectedRowIds.delete(index);
    } else {
      this.selectedRowIds.add(index);
    }
  }

  toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.previewRows.forEach((_, i) => this.selectedRowIds.add(i));
    } else {
      this.selectedRowIds.clear();
    }
  }

  goBack(): void {
    this.router.navigate(['../data-table'], { relativeTo: this.route });
  }

  previousStep(): void {
    this.currentStep--;
  }

  private parseNumber(str: string): { prefix: string; num: number; pad: number } | null {
    const match = str.match(/^(.*?)(\d+)$/);
    if (!match) return null;
    return { prefix: match[1], num: parseInt(match[2]), pad: match[2].length };
  }

  private formatNumber(prefix: string, num: number, pad: number): string {
    return prefix + String(num).padStart(pad, '0');
  }
}
